import type { GameContext, GameMiddleware } from "./types";
import { AnimationMiddleware } from "./animation-middleware";
import { WaterMiddleware } from "./water-middleware";
import { WeatherMiddleware } from "./weather-middleware";
import { ZoneEffectsMiddleware } from "./zone-effects-middleware";
import { NetworkingMiddleware } from "./networking-middleware";
import { GUIMiddleware } from "./gui-middleware";

/**
 * Manages a pipeline of GameMiddleware instances.
 *
 * Usage (extension-method style):
 *   const game = new Game();
 *   game.addAnimations().addWater().addWeather().addZoneEffects().addNetworking().addGui();
 *   await game.init(ctx);
 *   // in render loop:
 *   game.update(delta);
 *
 * Usage (composite / 1-liner style, preferred):
 *   game.use(new RoseOnlineMiddleware());
 *   await game.init(ctx);
 */
export class Game {
    private _middleware: GameMiddleware[] = [];
    // Tracks which middleware have completed init() — update() is only called
    // on middleware that are ready, since use() exposes a middleware to the
    // render loop immediately while init() may still be awaiting async work.
    private _initialized = new Set<GameMiddleware>();

    // ── Middleware registration ──────────────────────────────────────────────

    use(m: GameMiddleware): this {
        this._middleware.push(m);
        return this;
    }

    // ── Extension methods (fluent API for common middleware) ─────────────────

    /** Add THREE.AnimationHandler updates. */
    addAnimations(): this { return this.use(new AnimationMiddleware()); }

    /** Add underwater detection, scene fog, and UnderwaterOverlay. */
    addWater(): this { return this.use(new WaterMiddleware()); }

    /** Add weather effects (rain, thunder, leaves, volumetric fog). */
    addWeather(): this { return this.use(new WeatherMiddleware()); }

    /** Add zone-specific visual effects (volcano, ashfall, lava pools, etc.). */
    addZoneEffects(): this { return this.use(new ZoneEffectsMiddleware()); }

    /** Add networking infrastructure (login/world/game client globals). */
    addNetworking(): this { return this.use(new NetworkingMiddleware()); }

    /** Add game GUI (template loading, debug dialog). */
    addGui(): this { return this.use(new GUIMiddleware()); }

    // ── Lifecycle ────────────────────────────────────────────────────────────

    async init(ctx: GameContext): Promise<void> {
        for (const m of this._middleware) {
            await m.init(ctx);
            this._initialized.add(m);
        }
    }

    update(delta: number): void {
        for (const m of this._middleware) {
            if (this._initialized.has(m)) {
                m.update?.(delta);
            }
        }
    }

    destroy(): void {
        for (const m of this._middleware) {
            m.destroy?.();
        }
        this._middleware = [];
        this._initialized.clear();
    }

    onModeChange(mode: "editor" | "preview"): void {
        for (const m of this._middleware) {
            m.onModeChange?.(mode);
        }
    }

    // ── Lookup ───────────────────────────────────────────────────────────────

    /**
     * Returns the first registered middleware that is an instance of `ctor`,
     * or undefined if none is registered.
     */
    get<T extends GameMiddleware>(ctor: new (...args: any[]) => T): T | undefined {
        return this._middleware.find((m): m is T => m instanceof ctor);
    }
}
